// src/components/reports/ReportsHomeSlice.tsx — the "From Reports" slice for the
// very bottom of the Home page. Self-contained: it fetches its own snapshot +
// pin and renders nothing unless the user can see Reports AND has pinned a
// measure, so HomePage just drops it in. `canSee` is passed from HomePage's
// existing /api/me so we don't trigger a second identity fetch.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getReportsSnapshot, getReportsPin, putReportsPin } from "../../lib/reports";
import type { MeasureId, ReportsSnapshot } from "../../lib/reports";
import { PinnedMeasureStrip } from "./PinnedMeasureCard";

export function ReportsHomeSlice({ canSee }: { canSee: boolean }) {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<ReportsSnapshot | null>(null);
  const [pin, setPin] = useState<MeasureId | null>(null);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    (async () => {
      try {
        const [snap, p] = await Promise.all([getReportsSnapshot(), getReportsPin()]);
        if (!alive) return;
        setSnapshot(snap.payload);
        setPin(p.pin);
      } catch {
        /* Home must never break on the optional Reports slice. */
      }
    })();
    return () => {
      alive = false;
    };
  }, [canSee]);

  if (!canSee || !pin || !snapshot) return null;

  return (
    <PinnedMeasureStrip
      pinnedId={pin}
      snapshot={snapshot}
      onOpenReports={() => navigate("/reports")}
      onUnpin={() => {
        putReportsPin(null).catch(() => {});
        setPin(null);
      }}
    />
  );
}
